import * as z from "zod";
import { addBusinessDays } from "../date/business-day";

/**
 * shipmentEligibility controls which groups are eligible for allowed problem picks.
 * productEligibility controls which products on eligible groups are allowed to be
 * picked without inventory.
 *
 * If we ever need more complex logic than this, we should use filterGroups as the policy
 * and write a more complex evaluator
 */
export const AllowedProblemPickPolicySchema = z.object({
  shipmentEligibility: z.object({
    // Minimum order age in BUSINESS days (weekends + US holidays skipped).
    minOrderAgeDays: z.number().nullish(),
    shipmentTags: z.array(z.string()).nullish(),
  }),
  productEligibility: z.object({ vendors: z.array(z.string()).nullish() }),
});

export type AllowedProblemPickPolicy = z.infer<
  typeof AllowedProblemPickPolicySchema
>;

/**
 * Computes the instant at which a line item becomes eligible to be picked
 * without inventory under the policy, or null if it never is.
 *
 * Returning an absolute timestamp lets the index compare it against `now` at
 * query/display time, so eligibility flips as time passes with no reindex.
 *
 * Both tiers must pass:
 *  - productEligibility: the product's vendor is allow-listed. This is the
 *    allow-list, so an empty vendor list means nothing is eligible.
 *  - shipmentEligibility: OR'd — eligible immediately when the shipment has an
 *    allowed tag, otherwise once the order reaches the minimum age in business
 *    days (weekends + US holidays skipped). This section
 *    is a narrowing filter, so when no shipment condition is configured at all,
 *    every shipment qualifies (vendor gate only).
 */
export function computeAllowedProblemPickEligibleAt(
  policy: AllowedProblemPickPolicy,
  context: {
    shipment: { orderDate: Date; tags: string[] };
    product: { vendor: string | null };
  },
): Date | null {
  const { shipment, product } = context;

  const vendors = policy.productEligibility.vendors ?? [];
  const productEligible =
    product.vendor != null && vendors.includes(product.vendor);
  if (!productEligible) {
    return null;
  }

  const shipmentTags = policy.shipmentEligibility.shipmentTags ?? [];
  const minOrderAgeDays = policy.shipmentEligibility.minOrderAgeDays;

  // No shipment condition configured → all shipments qualify (vendor gate only).
  if (shipmentTags.length === 0 && minOrderAgeDays == null) {
    return shipment.orderDate;
  }

  // Conditions are OR'd; return the earliest instant the shipment qualifies.
  // Tags are compared case-insensitively: the settings UI lowercases tag
  // selections, while a fulfillment group's tags retain their original case.
  const lowerShipmentTags = new Set(
    shipmentTags.map((tag) => tag.toLowerCase()),
  );
  const tagEligible =
    lowerShipmentTags.size > 0 &&
    shipment.tags.some((tag) => lowerShipmentTags.has(tag.toLowerCase()));
  if (tagEligible) {
    return shipment.orderDate;
  }

  if (minOrderAgeDays != null) {
    // Order age is measured in business days, using the shared business-day
    // helpers (weekends + US holidays skipped). Counted in UTC so the index and
    // pick-wave agree on the eligibility instant.
    const orderZdt = Temporal.Instant.fromEpochMilliseconds(
      shipment.orderDate.getTime(),
    ).toZonedDateTimeISO("UTC");
    const eligibleZdt = addBusinessDays({
      date: orderZdt,
      businessDays: minOrderAgeDays,
      businessEntity: undefined,
    });
    return new Date(eligibleZdt.toInstant().epochMilliseconds);
  }

  return null;
}
